'use client';
import React from 'react';
import { AlertTriangle, Package, XCircle } from 'lucide-react';

const alerts = [
  {
    id: 'inv-001',
    item: 'NexusCore Enterprise — Tier 3 License',
    sku: 'NCE-T3-2026',
    stock: 4,
    threshold: 20,
    severity: 'critical' as const,
  },
  {
    id: 'inv-002',
    item: 'DataSync Pro — Annual Subscription',
    sku: 'DSP-ANN-2026',
    stock: 11,
    threshold: 25,
    severity: 'warning' as const,
  },
  {
    id: 'inv-003',
    item: 'SecureVault API — Enterprise Bundle',
    sku: 'SVA-ENT-2026',
    stock: 7,
    threshold: 15,
    severity: 'critical' as const,
  },
  {
    id: 'inv-004',
    item: 'MobileConnect SDK — Developer Pack',
    sku: 'MCS-DEV-2026',
    stock: 18,
    threshold: 30,
    severity: 'warning' as const,
  },
  {
    id: 'inv-005',
    item: 'FlowDesk Starter — Team License',
    sku: 'FDS-TM-2026',
    stock: 2,
    threshold: 10,
    severity: 'critical' as const,
  },
];

const severityConfig = {
  critical: {
    label: 'Critical',
    icon: XCircle,
    badge: 'bg-negative/10 text-negative',
    bar: 'bg-negative',
    row: 'border-l-2 border-negative',
  },
  warning: {
    label: 'Low Stock',
    icon: AlertTriangle,
    badge: 'bg-warning/10 text-warning',
    bar: 'bg-warning',
    row: 'border-l-2 border-warning',
  },
};

export default function InventoryAlerts() {
  const criticalCount = alerts.filter((a) => a.severity === 'critical').length;

  return (
    <div className="card-surface p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-foreground">Inventory Alerts</h2>
          <p className="text-sm text-muted-foreground">Low-stock license pools</p>
        </div>
        {criticalCount > 0 && (
          <span className="flex items-center gap-1.5 bg-negative/10 text-negative text-xs font-semibold rounded-full px-2.5 py-1">
            <AlertTriangle size={12} />
            {criticalCount} critical
          </span>
        )}
      </div>

      <div className="space-y-2.5">
        {alerts.map((alert) => {
          const config = severityConfig[alert.severity];
          const IconComp = config.icon;
          const pct = Math.round((alert.stock / alert.threshold) * 100);
          return (
            <div
              key={alert.id}
              className={`rounded-lg p-3 bg-muted/50 ${config.row}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-start gap-2 min-w-0">
                  <Package size={14} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-foreground truncate leading-tight">
                      {alert.item}
                    </p>
                    <p className="text-xs text-muted-foreground font-mono">{alert.sku}</p>
                  </div>
                </div>
                <span className={`label-caps rounded-full px-2 py-0.5 flex-shrink-0 ${config.badge}`}>
                  {config.label}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${config.bar}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                  {alert.stock} / {alert.threshold} units
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}